/* eslint-disable */
import React, { useState, useEffect, useContext, createRef, useRef } from "react";
import { Link } from "react-router-dom";

/* import url and gateway methods */
import { getData } from "../../../utils/Gateway";
import * as url from "../../../urlhelpers/UrlHelper";

// kendo react modules
import "@progress/kendo-theme-material/dist/all.css";
import { Grid, GridColumn } from "@progress/kendo-react-grid";

// ES2015 module syntax
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";

// import context
import { GlobalProvider } from "context/GlobalContext";

// import images
import { assetImages } from "constants";

// import translation function for language translation
import { useTranslation } from "react-i18next";

// ─────────────────────────────────────────────────────────────────────────────
// Map Kendo Grid field names → the exact query param name the backend expects.
// Add any new column here if you add more GridColumns in future.
// ─────────────────────────────────────────────────────────────────────────────
const FIELD_TO_PARAM = {
  username:    "username",    // maps to req.query.username  → searches name/surname
  usersurname: "usersurname", // maps to req.query.usersurname
  email:       "email",       // maps to req.query.email
  phone:       "phone",       // maps to req.query.phone
  companydata: "companydata", // maps to req.query.companydata
  "role.name": "rolename",    // maps to req.query.rolename  (role is a nested field)
  tagstring:   "tagstring",   // maps to req.query.tagstring (computed field)
  city:        "city",        // maps to req.query.city
  state:       "state",       // maps to req.query.state
  country:     "country",     // maps to req.query.country
  zipcode:     "zipcode",     // maps to req.query.zipcode
};

const UserListArea = ({
  downloadExcel,
  search,
  setRecords,
  checkedRecordCount,
}) => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const {
    isUserListLoading,
    setIsUserListLoading,
    showUserList,
    setShowUserList,
    setEditUserData,
    setCopyUserData,
    showUserNameColumn,
    showUserSurnameColumn,
    showUserRoleColumn,
    showUserEmailColumn,
    showUserTagColumn,
    showCompanyColumn,
    showUserPhoneColumn,
    showUserCityColumn,
    showUserZipcodeColumn = true,
    showUserStateColumn,
    showUserCountryColumn,
    searchUserRoles,
    searchUserTags,
    searchUserStatus,
    searchUserZipcodes,
    searchUserCityList,
    searchUserStateList,
    searchUserCountryList,
    screenHeight,
    searchUserIsCompany,
  } = useContext(GlobalProvider);

  // ── State ────────────────────────────────────────────────────────────────────
  const [userData, setUserData] = useState([]);
  const [dataState, setDataState] = useState({});
  const [kendoFilter, setKendoFilter] = useState(null); // last Kendo filter object
  const debounceTimer = useRef(null);

  // ── Build request URL ────────────────────────────────────────────────────────
  // All filtering goes through here — sidebar filters, Kendo column filters,
  // and the top search bar. Backend just reads plain query params.
  const buildRequestUrl = (activeFilter = null, currentSearch = search) => {
    let requestUrl =
      url.API_BASEURL + url.API_GET_ALL_USER + `?token=${token}&limit=100`;

    // ── Sidebar / context filters ──────────────────────────────────────────
    if (searchUserRoles.length > 0)       requestUrl += `&userrole=${searchUserRoles}`;
    if (searchUserTags.length > 0)        requestUrl += `&usertag=${searchUserTags}`;
    if (searchUserStatus.length > 0)      requestUrl += `&userstatus=${searchUserStatus}`;
    if (searchUserZipcodes.length > 0)    requestUrl += `&userzipcode=${searchUserZipcodes}`;
    if (searchUserCityList.length > 0)    requestUrl += `&usercity=${searchUserCityList}`;
    if (searchUserStateList.length > 0)   requestUrl += `&userstate=${searchUserStateList}`;
    if (searchUserCountryList.length > 0) requestUrl += `&usercountry=${searchUserCountryList}`;
    if (searchUserIsCompany)              requestUrl += `&useriscompany=${searchUserIsCompany}`;

    // ── Kendo column filter boxes ──────────────────────────────────────────
    // Each active filter sends its mapped param name + value to the backend.
    // The presence of ANY filter_op param tells the backend to use AND logic.
    if (activeFilter?.filters?.length > 0) {
      let hasValidFilter = false;

      activeFilter.filters.forEach(({ field, value, operator }) => {
        if (value !== "" && value !== undefined && value !== null) {
          const paramName = FIELD_TO_PARAM[field] ?? field; // fallback to field name
          requestUrl += `&${paramName}=${encodeURIComponent(value)}`;
          hasValidFilter = true;
        }
      });

      // Signal to backend that it should apply column-filter (AND) logic
      if (hasValidFilter) {
        requestUrl += `&filter_op=column`;
      }
    }

    // ── Top search bar (global search across all fields) ───────────────────
    if (currentSearch?.trim()) {
      requestUrl += `&globalsearch=${encodeURIComponent(currentSearch.trim())}`;
    }

    return requestUrl;
  };

  // ── Main fetch ───────────────────────────────────────────────────────────────
  const getAllUserList = async (activeFilter = null, currentSearch = search) => {
    if (!["admin", "manager"].includes(userInfo?.role?.name)) {
      setUserData([]);
      return;
    }

    try {
      setIsUserListLoading(true);

      const requestUrl = buildRequestUrl(activeFilter, currentSearch);
      console.log("user list url========>", requestUrl);

      const response = await getData(requestUrl);
      console.log("user list response------>", response);

      if (response.status) {
        setUserData(response.data);
        setRecords(response.data.length);
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsUserListLoading(false);
    }
  };

  // ── Kendo dataState change ────────────────────────────────────────────────────
  // Fires on every filter keystroke, sort click, or group change.
  const onDataStateChange = (event) => {
    const newDataState = event.dataState;
    setDataState(newDataState);

    const incomingFilter = newDataState.filter ?? null;
    setKendoFilter(incomingFilter);

    // Debounce: wait 500ms after user stops typing before calling API
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      getAllUserList(incomingFilter);
    }, 500);
  };

  // ── Initial load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getAllUserList();
  }, []);

  // ── Top search bar ───────────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      getAllUserList(kendoFilter, search);
    }, 500);
  }, [search]);

  // ── Sidebar context filter changes ───────────────────────────────────────────
  useEffect(() => {
    if (
      searchUserRoles.length > 0 ||
      searchUserTags.length > 0 ||
      searchUserStatus.length > 0 ||
      searchUserZipcodes.length > 0 ||
      searchUserCityList.length > 0 ||
      searchUserStateList.length > 0 ||
      searchUserCountryList.length > 0 ||
      searchUserIsCompany
    ) {
      getAllUserList(kendoFilter);
    }
  }, [
    searchUserRoles,
    searchUserTags,
    searchUserStatus,
    searchUserZipcodes,
    searchUserCityList,
    searchUserStateList,
    searchUserCountryList,
    searchUserIsCompany,
  ]);

  // ── Refresh trigger from parent (e.g. after save / delete) ───────────────────
  useEffect(() => {
    if (showUserList) {
      getAllUserList(kendoFilter);
      setShowUserList(false);
    }
  }, [showUserList]);

  // ── Excel export ─────────────────────────────────────────────────────────────
  const _exporter = createRef();
  const exportExcel = () => _exporter.current?.save();
  useEffect(() => {
    downloadExcel && exportExcel();
  }, [downloadExcel]);

  // ── Checkbox helpers ─────────────────────────────────────────────────────────
  const handelSelectedCount = () => {
    const checkboxes = document.querySelectorAll(
      'input[name="selectUserRecord"]'
    );
    let totalSelected = 0;
    let filteredData = [];
    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        totalSelected++;
        const found = userData.find((user) => user._id === checkbox.value);
        found && filteredData.push(found);
      }
    });
    checkedRecordCount(totalSelected);
    return filteredData;
  };

  const selectAllRecords = (e) => {
    document
      .querySelectorAll('.k-master-row input[name="selectUserRecord"]')
      .forEach((checkbox) => {
        checkbox.checked = e.target.checked;
      });
    handelSelectedCount();
  };

  // ── Custom cell renderers ─────────────────────────────────────────────────────
  const SelectRecordHeader = () => (
    <div className="d-flex align-items-center">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="selectAllMain"
          onClick={selectAllRecords}
        />
      </div>
    </div>
  );

  const SelectRecordRow = (props) => (
    <td>
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          value={props.dataItem._id}
          data-emailstring={props.dataItem.email}
          name="selectUserRecord"
          filtername="selectedrecord"
          onClick={handelSelectedCount}
        />
      </div>
    </td>
  );

  const nameHtml = (props) => (
    <td className="kendoCustomColumn overflow-visible">
      <div className="d-flex align-items-center gap-2">
        <p className="d-flex gap-2 align-items-center text-nowrap">
          <span className="d-block icon-user">
            <img
              src={
                props?.dataItem?.photoimage
                  ? url.SERVER_URL + props?.dataItem?.photoimage?.path
                  : assetImages?.defaultUser
              }
              alt=""
            />
          </span>
          <span
            className="d-block"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={`${props.dataItem.username} ${props.dataItem.usersurname}`}
          >
            {props.dataItem.username}
          </span>
        </p>

        <div className="ms-auto d-flex align-items-center gap-1">
          <Link to="#">
            <span
              className={`d-block material-symbols-outlined icon-md ${
                props.dataItem.allowsendingmail ? "text-green" : "text-danger"
              }`}
            >
              mail
            </span>
          </Link>

          {props.dataItem?.iscompany ? (
            <Link to="#">
              <span className="d-block material-symbols-outlined icon-md text-green">
                business
              </span>
            </Link>
          ) : (
            <Link to="#">
              <span
                className={`d-block material-symbols-outlined icon-md ${
                  props.dataItem.zipcode ? "text-green" : "text-danger"
                }`}
              >
                location_on
              </span>
            </Link>
          )}

          <div className="dropdown cell-dropdown mt-2">
            <button
              className="bg-transparent p-0 border-0"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span className="d-block material-symbols-outlined">
                more_vert
              </span>
            </button>
            <ul className="dropdown-menu border-0 shadow-lg">
              <li>
                <Link
                  className="dropdown-item"
                  to="#saveUserPopup"
                  data-bs-toggle="offcanvas"
                  onClick={() => setEditUserData(props.dataItem._id)}
                >
                  {t("Edit")}
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item"
                  to={`/profile/${props.dataItem._id}`}
                >
                  {t("View Profile")}
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item"
                  to="#saveUserPopup"
                  data-bs-toggle="offcanvas"
                  onClick={() => setCopyUserData(props.dataItem._id)}
                >
                  {t("Copy User")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </td>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="table-responsive">
      {/* Excel export (hidden, triggered by exportExcel()) */}
      <ExcelExport
        data={
          handelSelectedCount().length > 0 ? handelSelectedCount() : userData
        }
        fileName="User Management.xlsx"
        ref={_exporter}
      >
        <ExcelExportColumn field="username"      title={t("Name")}         locked={true} width={200} />
        <ExcelExportColumn field="usersurname"   title={t("Surname")} />
        <ExcelExportColumn field="role.name"     title={t("Role")} />
        <ExcelExportColumn field="email"         title={t("Email")} />
        <ExcelExportColumn field="address"       title={t("Address")} />
        <ExcelExportColumn field="tagstring"     title={t("Tags")} />
        <ExcelExportColumn field="companydata"   title={t("Company")} />
        <ExcelExportColumn field="phone"         title={t("Phone")} />
        <ExcelExportColumn field="city"          title={t("City")} />
        <ExcelExportColumn field="state"         title={t("State")} />
        <ExcelExportColumn field="country"       title={t("Country")} />
        <ExcelExportColumn field="zipcode"       title={t("Zip Code")} />
        <ExcelExportColumn field="language"      title={t("Language")} />
        <ExcelExportColumn field="costperhour"   title={t("Cost Per Hour")} />
        <ExcelExportColumn field="iscompany"     title={t("Is Company")} />
        <ExcelExportColumn field="mapcoordinate" title={t("Map Url")} />
        <ExcelExportColumn field="latitude"      title={t("Latitude")} />
        <ExcelExportColumn field="longitude"     title={t("Longitude")} />
      </ExcelExport>

      {/*
        Single Grid — all filtering (column boxes, sidebar, top search bar)
        flows through getAllUserList() → API. No client-side JS filtering.
      */}
      <Grid
        style={{ height: screenHeight }}
        data={isUserListLoading ? [] : userData}
        filterable={true}
        sortable={true}
        groupable={false}
        onDataStateChange={onDataStateChange}
        {...dataState}
        reorderable
      >
        <GridColumn
          field="id"
          headerCell={SelectRecordHeader}
          cell={SelectRecordRow}
          filterable={false}
          sortable={false}
          width="70px"
        />

        {showUserNameColumn && (
          <GridColumn
            field="username"
            title={t("Name")}
            cell={nameHtml}
            width="400px"
          />
        )}
        {showUserSurnameColumn && (
          <GridColumn
            field="usersurname"
            title={t("Surname")}
            width="400px"
          />
        )}
        {showUserEmailColumn && (
          <GridColumn field="email" title={t("Email")} width="400px" />
        )}
        {showCompanyColumn && (
          <GridColumn field="companydata" title={t("Company")} width="300px" />
        )}
        {showUserRoleColumn && (
          <GridColumn field="role.name" title={t("Role")} width="300px" />
        )}
        {showUserTagColumn && (
          <GridColumn field="tagstring" title={t("Tags")} width="250px" />
        )}
        {showUserPhoneColumn && (
          <GridColumn field="phone" title={t("Phone")} width="250px" />
        )}
        {showUserCityColumn && (
          <GridColumn field="city" title={t("City")} width="250px" />
        )}
        {showUserZipcodeColumn && (
          <GridColumn field="zipcode" title={t("Zip Code")} width="250px" />
        )}
        {showUserStateColumn && (
          <GridColumn field="state" title={t("State")} width="250px" />
        )}
        {showUserCountryColumn && (
          <GridColumn field="country" title={t("Country")} width="250px" />
        )}
      </Grid>
    </div>
  );
};

export default UserListArea;  